import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";
import { useProfile } from "../context/ProfileContext";

import PersonalInformation from "../components/PersonalInformation";
import LanguagePreferences from "../components/LanguagePreferences";
import AadhaarDetails from "../components/AadhaarDetails";
import PANDetails from "../components/PANDetails";
import BankDetails from "../components/BankDetails";
import DrivingLicenseDetails from "../components/DrivingLicenseDetails";
import DrivingChoices from "../components/DrivingChoices";
import TrainingVideos from "../components/TrainingVideos";
import DriverRideAnalytics from "../components/DriverRideAnalytics";
import {
  FaUserCircle,
  FaUser,
  FaChartBar,
  FaCar,
  FaClipboardList,
  FaSpinner,
} from "react-icons/fa";
import ProfilePictureUploader from "../components/ProfilePictureUploader";

const DriverDetails = () => {
  const { profile, assignModule } = useProfile();
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testRecordUpdating, setTestRecordUpdating] = useState(false);
  const [status, setStatus] = useState("");
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [activeTab, setActiveTab] = useState("onboarding");
  const { id } = useParams();
  const isNewType = profile?.employeeType === "new";
  const driverModule = isNewType && assignModule?.driver?.enabled ? assignModule.driver : null;

  // Call Status Form State
  const [callStatusForm, setCallStatusForm] = useState({
    contactSource: "",
    language: "",
    assignee: "",
    reporter: "",
    date: "",
    type: "",
    status: "",
    notes: "",
    customStatusValue: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [callStatusHistory, setCallStatusHistory] = useState([]);

  const parseFlexibleDate = (value) => {
    if (!value) return null;

    const raw = String(value).trim();

    const ddmmyyyyMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})(?:[-T ].*)?$/);
    if (ddmmyyyyMatch) {
      const day = Number(ddmmyyyyMatch[1]);
      const month = Number(ddmmyyyyMatch[2]);
      const year = Number(ddmmyyyyMatch[3]);
      const parsed = new Date(Date.UTC(year, month - 1, day));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const yyyymmddMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
    if (yyyymmddMatch) {
      const year = Number(yyyymmddMatch[1]);
      const month = Number(yyyymmddMatch[2]);
      const day = Number(yyyymmddMatch[3]);
      const parsed = new Date(Date.UTC(year, month - 1, day));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const toInputDateValue = (value) => {
    const parsed = parseFlexibleDate(value);
    return parsed ? parsed.toISOString().split("T")[0] : "";
  };

  const canEditSection = () => {
    if (isNewType && driverModule) {
      const allowed = Array.isArray(driverModule.allowedStatuses) ? driverModule.allowedStatuses : [];
      const statusAllowed = allowed.length === 0 || allowed.includes(driver?.status);
      return driver && driverModule.actions?.edit && statusAllowed;
    }
    if (currentUserRole === "superadmin") {
      return true;
    }
    if (
      currentUserRole === "senior" &&
      ["InProgress", "Revisit", "Pending", "Rejected", "Verified"].includes(
        driver?.status
      )
    ) {
      return true;
    }
    if (
      currentUserRole === "junior" &&
      ["InProgress", "Revisit", "Pending", "Rejected"].includes(driver?.status)
    ) {
      return true;
    }
    return false;
  };

  // For new-type: section edit is based only on allowedStatuses (not main Actions > Edit). Used by DL/Aadhaar/PAN Edit details & Edit status.
  const statusAllowedForDriver = () => {
    if (!driver) return false;
    if (!isNewType || !driverModule) return true;
    const allowed = Array.isArray(driverModule.allowedStatuses) ? driverModule.allowedStatuses : [];
    return allowed.length === 0 || allowed.includes(driver?.status);
  };

  const shouldHidePhone = () =>
    isNewType && driverModule && !!driverModule.hidePhoneNumber;

  const canShowPhone = () => !shouldHidePhone();

  const canEditPhone = () => {
    if (shouldHidePhone()) return false;
    if (isNewType && driverModule) return !!driverModule.phoneEdit;
    return canEditSection();
  };

  // Section edit flags (Edit details / Edit status per DL/Aadhaar/PAN) apply only on this detail page; they do not affect the main Drivers list.
  // For new-type users, DL/Aadhaar/PAN editability is driven only by section Yes/No + Edit details / Edit status (not by Actions > Edit).
  const canEditDL = () => {
    if (isNewType && driverModule) return !!driverModule.dlStatus && statusAllowedForDriver();
    return canEditSection();
  };

  const canEditAadhaar = () => {
    if (isNewType && driverModule) return !!driverModule.aadhaarStatus && statusAllowedForDriver();
    return canEditSection();
  };

  const canEditPAN = () => {
    if (isNewType && driverModule) return !!driverModule.panStatus && statusAllowedForDriver();
    return canEditSection();
  };

  const canEditDLDetails = () => {
    if (currentUserRole === "superadmin") return true;
    if (isNewType && driverModule) return !!driverModule.dlStatus && !!driverModule.dlEditDetails && statusAllowedForDriver();
    return canEditDL();
  };
  const canEditDLStatus = () => {
    if (currentUserRole === "superadmin") return true;
    if (isNewType && driverModule) return !!driverModule.dlStatus && !!driverModule.dlEditStatus && statusAllowedForDriver();
    return canEditDL();
  };
  const canEditAadhaarDetails = () => {
    if (currentUserRole === "superadmin") return true;
    if (isNewType && driverModule) return !!driverModule.aadhaarStatus && !!driverModule.aadhaarEditDetails && statusAllowedForDriver();
    return canEditAadhaar();
  };
  const canEditAadhaarStatus = () => {
    if (currentUserRole === "superadmin") return true;
    if (isNewType && driverModule) return !!driverModule.aadhaarStatus && !!driverModule.aadhaarEditStatus && statusAllowedForDriver();
    return canEditAadhaar();
  };
  const canEditPANDetails = () => {
    if (currentUserRole === "superadmin") return true;
    if (isNewType && driverModule) return !!driverModule.panStatus && !!driverModule.panEditDetails && statusAllowedForDriver();
    return canEditPAN();
  };
  const canEditPANStatus = () => {
    if (currentUserRole === "superadmin") return true;
    if (isNewType && driverModule) return !!driverModule.panStatus && !!driverModule.panEditStatus && statusAllowedForDriver();
    return canEditPAN();
  };

  const canEditDrivingChoices = () => {
    if (currentUserRole === "superadmin") return true;
    if (isNewType && driverModule) return !!driverModule.drivingChoicesStatus && !!driverModule.drivingChoicesEditDetails && statusAllowedForDriver();
    return canEditSection();
  };

  const showDL = !isNewType || !driverModule || !!driverModule.dlStatus;
  const showAadhaar = !isNewType || !driverModule || !!driverModule.aadhaarStatus;
  const showPAN = !isNewType || !driverModule || !!driverModule.panStatus;
  const showDrivingChoices = !isNewType || !driverModule || !!driverModule.drivingChoicesStatus;


  const fetchDriverDetails = async () => {
    try {
      const token = localStorage.getItem("adminToken");

      const [driverRes, profileRes] = await Promise.all([
        axiosInstance.get(`${API.ADMIN.DRIVER_DETAIL}/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axiosInstance.get(API.ADMIN.GET_ADMIN_PROFILE, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const driverData = driverRes.data.data;
      setDriver(driverData);
      setStatus(driverData.status);
      setCurrentUserRole(profileRes.data?.data?.role?.slug || "");

      // Load existing contactSource and language
      // Get the latest callStatusHistory entry if available
      const latestCallStatus = driverData.callStatusHistory && driverData.callStatusHistory.length > 0
        ? driverData.callStatusHistory[driverData.callStatusHistory.length - 1]
        : null;

      // Format date for input field (YYYY-MM-DD)
      let formattedDate = "";
      if (latestCallStatus && latestCallStatus.date) {
        formattedDate = toInputDateValue(latestCallStatus.date);
      }

      // Load form data from driver profile
      setCallStatusForm({
        contactSource: driverData.contactSource || "",
        language: driverData.language || "",
        assignee: driverData.assignee || driverData.assignedEmployee?.fullName || "",
        reporter: driverData.reporter || "",
        date: formattedDate,
        type: latestCallStatus?.type || "",
        status: latestCallStatus?.status || "",
        notes: latestCallStatus?.notes || "",
        customStatusValue: latestCallStatus?.customStatusValue ?? "",
      });

      // Load callStatusHistory for display
      setCallStatusHistory(driverData.callStatusHistory || []);
    } catch (error) {
      console.error("Error fetching driver or profile details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriverDetails();
  }, [id]);

  // Handle Call Status Form Submission
  const handleCallStatusSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      const token = localStorage.getItem("adminToken");

      // Prepare payload for PATCH API
      const payload = {};

      // Add fullName and phone from driver data
      if (driver?.fullName) {
        payload.fullName = driver.fullName;
      }
      if (driver?.phone && canShowPhone()) {
        payload.phone = driver.phone;
      }

      // Add contactSource if provided
      if (callStatusForm.contactSource) {
        payload.contactSource = callStatusForm.contactSource;
      }

      // Add language if provided
      if (callStatusForm.language) {
        payload.language = callStatusForm.language;
      }

      // Add assignee if provided
      if (callStatusForm.assignee && callStatusForm.assignee.trim()) {
        payload.assignee = callStatusForm.assignee.trim();
      }

      // Add reporter if provided
      if (callStatusForm.reporter && callStatusForm.reporter.trim()) {
        payload.reporter = callStatusForm.reporter.trim();
      }

      // Only add callStatusHistoryEntry when user provided at least one of date, type, or status (or Custom Filed)
      const hasCallStatusData =
        callStatusForm.date ||
        callStatusForm.type ||
        callStatusForm.status ||
        callStatusForm.status === "Custom Filed";
      if (hasCallStatusData) {
        payload.callStatusHistoryEntry = {
          date: callStatusForm.date,
          type: callStatusForm.type,
          status: callStatusForm.status,
          customStatusValue: callStatusForm.customStatusValue || "",
          notes: callStatusForm.notes || "",
        };
      }

      const response = await axiosInstance.patch(
        `${API.ADMIN.UPDATE_DRIVER}/${id}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        toast.success("Driver profile updated successfully!");
        // Refresh driver details to get updated callStatusHistory
        await fetchDriverDetails();
      } else {
        toast.error(response.data.message || "Failed to update driver profile");
      }
    } catch (error) {
      console.error("Error updating driver profile:", error);
      toast.error(
        error.response?.data?.message || "Failed to update driver profile"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle form field changes
  const handleCallStatusChange = (field, value) => {
    setCallStatusForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  {
    console.log(driver);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 py-10 px-6">
        <div className="flex flex-col justify-center items-center py-20">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <FaUserCircle className="text-6xl text-blue-500 mb-4 mx-auto" />
            <FaSpinner className="animate-spin text-3xl text-blue-500 mb-4 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Loading Driver Details
            </h2>
            <p className="text-gray-600 mb-4">
              Please wait while we fetch driver information...
            </p>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.1s" }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "0.2s" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="min-h-screen bg-gray-100 py-10 px-6">
        <div className="flex flex-col justify-center items-center py-20">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <FaUserCircle className="text-6xl text-gray-400 mb-4 mx-auto" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Driver Not Found
            </h2>
            <p className="text-gray-600 mb-4">
              The requested driver could not be found.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: "onboarding",
      label: "Onboarding Details",
      icon: FaClipboardList,
    },
    {
      id: "analytics",
      label: "Ride Analytics",
      icon: FaChartBar,
    },
  ];

  const renderTabContent = () => {
    const emptyFields = driver.emptyFields || [];
    const isFieldEmpty = (fieldName) => emptyFields.includes(fieldName);

    switch (activeTab) {
      case "onboarding":
        return (
          <div className="space-y-0">
            {/* Info Sections */}
            <PersonalInformation
              driver={driver}
              editable={canEditSection()}
              phoneVisible={canShowPhone()}
              phoneEditable={canEditPhone()}
              onUpdated={fetchDriverDetails}
              showIfEmpty={isFieldEmpty("fullName")}
            />
            <LanguagePreferences
              languagePreference={driver.languagePreference}
            />
            {showDL && (
              <DrivingLicenseDetails
                drivingLicense={driver.drivingLincenseDetails}
                driverId={driver._id}
                editable={driverModule ? undefined : canEditDL()}
                editableDetails={driverModule ? canEditDLDetails() : undefined}
                editableStatus={driverModule ? canEditDLStatus() : undefined}
                currentUserRole={currentUserRole}
                showIfEmpty={isFieldEmpty("drivingLincenseDetails")}
                onUpdated={fetchDriverDetails}
              />
            )}
            {showAadhaar && (
              <AadhaarDetails
                aadhaar={driver.aadhaarDetails}
                driverId={driver._id}
                editable={driverModule ? undefined : canEditAadhaar()}
                editableDetails={driverModule ? canEditAadhaarDetails() : undefined}
                editableStatus={driverModule ? canEditAadhaarStatus() : undefined}
                currentUserRole={currentUserRole}
                hideOnboardedStatus={isNewType}
                showIfEmpty={isFieldEmpty("aadhaarDetails")}
                onUpdated={fetchDriverDetails}
              />
            )}
            {showPAN && (
              <PANDetails
                pan={driver.panDetails}
                driverId={driver._id}
                editable={driverModule ? undefined : canEditPAN()}
                editableDetails={driverModule ? canEditPANDetails() : undefined}
                editableStatus={driverModule ? canEditPANStatus() : undefined}
                currentUserRole={currentUserRole}
                hideOnboardedStatus={isNewType}
                showIfEmpty={isFieldEmpty("panDetails")}
                onUpdated={fetchDriverDetails}
              />
            )}
            <BankDetails
              bank={driver.bankAccountDetails}
              editable={canEditSection()}
              currentUserRole={currentUserRole}
            />
            {showDrivingChoices && (
              <DrivingChoices
                drivingChoices={driver.drivingChoices}
                editable={driverModule ? canEditDrivingChoices() : canEditSection()}
                showIfEmpty={isFieldEmpty("drivingChoices")}
                driverId={driver._id}
                onUpdated={fetchDriverDetails}
              />
            )}
            {/* <TrainingVideos
              videos={driver.trainingVideos}
              verified={driver.trainingVerified}
              editable={canEditSection()}
              driverId={driver._id}
              currentUserRole={currentUserRole}
            /> */}

            {/* Contact & Call Status */}
            <div className="border-t border-gray-200 mt-8">
              <div className="px-6 py-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  Contact & Call Status
                </h3>

                <form className="space-y-6" onSubmit={handleCallStatusSubmit}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Source
                    </label>
                    <select
                      value={callStatusForm.contactSource}
                      onChange={(e) => handleCallStatusChange("contactSource", e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Contact Source</option>
                      <option value="WhatsApp Group">WhatsApp Group</option>
                      <option value="Google Ads">Google Ads</option>
                      <option value="Meta Ads">Meta Ads</option>
                      <option value="Google Search Organic">Google Search Organic</option>
                      <option value="Job Posting Paid">Job Posting Paid</option>
                      <option value="Job Posting Organic">Job Posting Organic</option>
                      <option value="Apna Jobs Paid">Apna Jobs Paid</option>
                      <option value="Workindia Paid">Workindia Paid</option>
                      <option value="OLX Paid">OLX Paid</option>
                      <option value="Apna Jobs Organic">Apna Jobs Organic</option>
                      <option value="Workindia Organic">Workindia Organic</option>
                      <option value="OLX Organic">OLX Organic</option>
                      <option value="Custom">Custom</option>
                    </select>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      Call Status
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date
                        </label>
                        <input
                          type="date"
                          value={callStatusForm.date}
                          onChange={(e) => handleCallStatusChange("date", e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          required
                          value={callStatusForm.type}
                          onChange={(e) => handleCallStatusChange("type", e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Type</option>
                          <option value="Initial Call">Initial Call</option>
                          <option value="1st Followup">1st Followup</option>
                          <option value="2nd Followup">2nd Followup</option>
                          <option value="3rd Followup">3rd Followup</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status
                        </label>
                        <select
                          value={callStatusForm.status}
                          onChange={(e) => handleCallStatusChange("status", e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select Status</option>
                          <option value="Not Answered">Not Answered</option>
                          <option value="Number Not Valid">Number Not Valid</option>
                          <option value="Agreed to Fill the Form">Agreed to Fill the Form</option>
                          <option value="Agreed to Send Documents">Agreed to Send Documents</option>
                          <option value="Not Interested">Not Interested</option>
                          <option value="Message Sent">Message Sent</option>
                          <option value="Docs Received">Docs Received</option>
                          <option value="Docs Uploaded">Docs Uploaded</option>
                          <option value="Custom Filed">Custom Filed</option>
                        </select>
                      </div>
                    </div>

                    {callStatusForm.status === "Custom Filed" && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Custom status value
                        </label>
                        <input
                          type="text"
                          value={callStatusForm.customStatusValue}
                          onChange={(e) => handleCallStatusChange("customStatusValue", e.target.value)}
                          placeholder="Enter custom status..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes
                      </label>
                      <textarea
                        value={callStatusForm.notes}
                        onChange={(e) => handleCallStatusChange("notes", e.target.value)}
                        placeholder="Enter any additional notes..."
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Language
                    </label>
                    <select
                      value={callStatusForm.language}
                      onChange={(e) => handleCallStatusChange("language", e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Language</option>
                      <option value="Hindi">Hindi</option>
                      <option value="Telugu">Telugu</option>
                      <option value="Tamil">Tamil</option>
                      <option value="Kannada">Kannada</option>
                      <option value="Marathi">Marathi</option>
                      <option value="Gujarati">Gujarati</option>
                      <option value="Custom Field">Custom Field</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assignee
                      </label>
                      <input
                        type="text"
                        value={callStatusForm.assignee}
                        onChange={(e) => handleCallStatusChange("assignee", e.target.value)}
                        placeholder="Enter assignee name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reporter
                      </label>
                      <input
                        type="text"
                        value={callStatusForm.reporter}
                        onChange={(e) => handleCallStatusChange("reporter", e.target.value)}
                        placeholder="Enter reporter name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {submitting ? (
                        <>
                          <FaSpinner className="animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        "Save Call Status"
                      )}
                    </button>
                  </div>
                </form>

                {callStatusHistory && callStatusHistory.length > 0 && (
                  <div className="mt-8">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      Call Status History
                    </h4>
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Notes
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Created At
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {callStatusHistory
                              .slice()
                              .reverse()
                              .map((entry, index) => {
                                const entryDate = new Date(entry.date);
                                const createdAt = new Date(entry.createdAt);
                                return (
                                  <tr key={entry._id || index} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {entryDate.toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {entry.type}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                        {entry.status === "Custom Filed"
                                          ? `Custom Filed - ${entry.customStatusValue?.trim() || ""}`.replace(/ - $/, "")
                                          : entry.status}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                      {entry.notes || "-"}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {createdAt.toLocaleString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case "analytics":
        return (
          <div className="p-6">
            <DriverRideAnalytics driverId={driver._id} driver={driver} />
          </div>
        );
      default:
        return null;
    }
  };

  const handleToggleTestRecord = async () => {
    try {
      if (!driver?._id) return;
      setTestRecordUpdating(true);
      const token = localStorage.getItem("adminToken");
      const nextValue = !Boolean(driver.isTestRecord);

      const response = await axiosInstance.patch(
        `${API.ADMIN.UPDATE_DRIVER}/${driver._id}`,
        { isTestRecord: nextValue },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response?.data?.success) {
        setDriver((prev) => (prev ? { ...prev, isTestRecord: nextValue } : prev));
        toast.success(nextValue ? "Test record enabled" : "Test record disabled");
      } else {
        toast.error(response?.data?.message || "Failed to update test record status");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update test record status"
      );
    } finally {
      setTestRecordUpdating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-6">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <ProfilePictureUploader
          driverId={driver._id}
          existingImage={driver.profileImage}
          fullName={driver.fullName}
          onUpdate={fetchDriverDetails}
          editable={canEditSection()}
        />

        {driver.status === "Onboarded" && driver.statusUpdatedAt && (
          <p className="px-6 pt-2 text-sm text-gray-600">
            Onboarded on {new Date(driver.statusUpdatedAt).toLocaleDateString("en-IN")}
          </p>
        )}

        {currentUserRole === "superadmin" && (
          <div className="px-6 pt-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Test Record Control</h3>
                <p className="text-xs text-gray-600 mt-1">
                  Mark this driver as test data to exclude from revenue metrics.
                </p>
                <span
                  className={`inline-flex mt-2 px-2 py-1 text-xs font-medium rounded-full ${
                    driver.isTestRecord
                      ? "bg-rose-100 text-rose-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {driver.isTestRecord ? "Enabled (True)" : "Disabled (False)"}
                </span>
              </div>

              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(driver.isTestRecord)}
                  onChange={handleToggleTestRecord}
                  disabled={testRecordUpdating}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-indigo-300 peer-checked:bg-indigo-600 transition-colors">
                  <span className="absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  {testRecordUpdating
                    ? "Updating..."
                    : driver.isTestRecord
                    ? "ON"
                    : "OFF"}
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="min-h-[600px]">{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default DriverDetails;
